import { getDownloadURL, ref } from 'firebase/storage'
import { useEffect, useState } from 'react'
import { appCheckZustand, storage } from './firebase'

/**
 * Bilder holt der Browser direkt aus Firebase Storage. Das Backend reicht nur
 * den Pfad durch — `core_media` hält Pfade, keine Ordnerkonvention, und eine
 * Datei wird **nie** verschoben oder überschrieben (§7.3, §9.2).
 *
 * Nur Lesen. In diesem Modul steht bewusst kein `uploadBytes`, kein
 * `deleteObject` und kein `updateMetadata`: Ein Downloadtoken hängt am Objekt,
 * und wer es rotiert, zerstört Bilder in der 1.0, die im App Store steht.
 */

const zwischenspeicher = new Map<string, Promise<string>>()

export function bildAdresse(pfad: string): Promise<string> {
  // Eine fertige Adresse wird durchgereicht. In `core_media.storage_path`
  // steht nie eine (1.990 Zeilen nachgesehen, keine einzige) — die Attrappe
  // kann damit aber lokal echte Bilder ausliefern, wo Firebase Storage nicht
  // erreichbar ist. Ein Zweig, der in der Produktion nie genommen wird, und
  // eine Zeile billiger als eine zweite Bildkomponente fürs Entwickeln.
  if (/^https?:\/\//i.test(pfad)) return Promise.resolve(pfad)

  const vorhanden = zwischenspeicher.get(pfad)
  if (vorhanden) return vorhanden

  const versprechen = getDownloadURL(ref(storage, pfad))
  zwischenspeicher.set(pfad, versprechen)
  // Ein Fehlschlag darf nicht dauerhaft hängenbleiben.
  versprechen.catch(() => zwischenspeicher.delete(pfad))
  return versprechen
}

export type Fehlerursache =
  /** 404 — unter dem Pfad liegt nichts. Datenstand. */
  | 'datenstand'
  /** 403 — die Leseregel lässt den Pfad nicht zu. Regelproblem. */
  | 'regel'
  /** 401 — App Check oder Anmeldung. */
  | 'zugang'
  | 'unbekannt'

export type Bildzustand =
  | { art: 'laedt' }
  | { art: 'da'; adresse: string }
  | { art: 'fehler'; meldung: string; ursache: Fehlerursache }

function fehlerCode(fehler: unknown): string {
  return typeof fehler === 'object' && fehler !== null && 'code' in fehler
    ? String((fehler as { code: unknown }).code)
    : ''
}

function istUnauthenticated(fehler: unknown): boolean {
  return fehlerCode(fehler) === 'storage/unauthenticated'
}

/**
 * Firebase-Storage-Fehler in einen Satz, der sagt, was zu tun ist.
 *
 * Der häufigste Fall im Bestand: `core_media.storage_path` zeigt noch auf
 * `photosInReview/`, während die Cloud Function der 1.0 die Datei längst nach
 * `photos/` verschoben hat. Das ist ein Datenstand, kein Anzeigefehler — und
 * die Oberfläche sagt es so, statt einen SDK-Fehler durchzureichen.
 *
 * Geraten wird hier nichts: `core_media` hält Pfade, keine Ordnerkonvention
 * (§7.3). Ein automatisches Ausweichen auf einen Nachbarordner würde genau
 * die Konvention einführen, die das Modell vermeidet.
 */
function fehlerText(fehler: unknown): { meldung: string; ursache: Fehlerursache } {
  const code = fehlerCode(fehler)

  switch (code) {
    case 'storage/object-not-found':
      return {
        meldung:
          'Unter diesem Pfad liegt keine Datei. Der Pfad steht so in ' +
          'core_media — die Datei fehlt im Bucket oder liegt woanders. ' +
          'Das ist ein Datenstand und gehört an die Datenseite.',
        ursache: 'datenstand',
      }
    case 'storage/unauthorized':
      return {
        meldung:
          'Die Leseregel in Firebase Storage lässt diesen Pfad nicht zu. ' +
          'Die Datei mag da sein — gelesen werden darf sie nicht. Das ist ' +
          'ein Regelproblem in storage.rules und gehört an den Schema-Chat.',
        ursache: 'regel',
      }
    case 'storage/unauthenticated':
      // Wird in `useBild` durch den echten Grund ersetzt: Das SDK bildet jede
      // 401 hierauf ab, auch die von App Check — und dann hilft kein Ab- und
      // Anmelden, so oft man es auch versucht.
      return {
        meldung: 'Storage hat die Anfrage mit 401 abgewiesen.',
        ursache: 'zugang',
      }
    default:
      return {
        meldung: fehler instanceof Error ? fehler.message : 'Bild nicht abrufbar',
        ursache: 'unbekannt',
      }
  }
}

/**
 * Lädt ein Bild, mit eingeplantem Rückfall.
 *
 * `ersatz` ist kein Notnagel: Die 400er-Variante entsteht **asynchron** durch
 * eine Cloud Function, während ein Datenbank-Trigger ihren Pfad sofort in
 * `variants` einträgt. Der Trigger behauptet die Datei, er erzeugt sie nicht.
 * Kurz nach einem Upload gibt es sie noch nicht, bei einer kaputten Quelldatei
 * nie — im Bestand fehlt sie bei 10 von 1845 Ortsfotos. Dann wird das Original
 * geladen, statt eine leere Fläche zu zeigen.
 */
export function useBild(
  pfad: string | null | undefined,
  ersatz?: string | null,
): Bildzustand {
  const [zustand, setZustand] = useState<Bildzustand>({ art: 'laedt' })

  useEffect(() => {
    if (!pfad && !ersatz) {
      setZustand({
        art: 'fehler',
        meldung: 'Kein Pfad hinterlegt.',
        ursache: 'datenstand',
      })
      return
    }
    let abgebrochen = false
    setZustand({ art: 'laedt' })

    const zuerst = pfad ?? (ersatz as string)
    const danach = pfad && ersatz && ersatz !== pfad ? ersatz : null

    // Fehlt die Variante, ist das der Normalfall und kein Fehler — deshalb
    // wird der zweite Pfad still versucht, bevor irgendetwas gemeldet wird.
    const laden = danach
      ? bildAdresse(zuerst).catch((fehler: unknown) => {
          if (fehlerCode(fehler) !== 'storage/object-not-found') throw fehler
          return bildAdresse(danach)
        })
      : bildAdresse(zuerst)

    laden
      .then((adresse) => {
        if (!abgebrochen) setZustand({ art: 'da', adresse })
      })
      .catch(async (fehler: unknown) => {
        if (abgebrochen) return
        const roh = fehlerText(fehler)

        // Bei einer 401 ist App Check der weitaus häufigere Grund als eine
        // fehlende Anmeldung. Nachfragen kostet nichts und erspart die Suche.
        if (istUnauthenticated(fehler)) {
          const zustand = await appCheckZustand()
          if (abgebrochen) return
          if (!zustand.ok) {
            setZustand({
              art: 'fehler',
              ursache: 'zugang',
              meldung:
                'App Check liefert kein gültiges Token — Firebase Storage ' +
                `weist deshalb jeden Bildabruf ab (${zustand.code}): ` +
                zustand.meldung,
            })
            return
          }
          setZustand({
            art: 'fehler',
            ursache: 'zugang',
            meldung:
              'Storage hat die Anfrage mit 401 abgewiesen, obwohl App Check ' +
              'ein Token liefert. Dann fehlt die Firebase-Anmeldung — bitte ' +
              'neu anmelden.',
          })
          return
        }

        setZustand({ art: 'fehler', ...roh })
      })

    return () => {
      abgebrochen = true
    }
  }, [pfad, ersatz])

  return zustand
}
